import React from 'react'
import PropTypes from 'prop-types'
import {Flex, Box, Text, HStack, Stack, useStyleConfig, Image, Divider} from '@chakra-ui/react'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import QuantityPicker from '../../../../components/quantity-picker'
import VatLabel from '../../../../components/vat-label'
import UnitPrice from '../../../cart/partials/unit-price'
import {FormattedMessage, useIntl} from 'react-intl'
import {remCalc} from '../../../../theme/_utils'
import {useBooleanCustomPreference} from '../../../../hooks/use-custom-preference'
import {getItemPriceByStatus, getInfoLabelVariantByOmsStatus} from '../utils'
import {SFCC_ORDER_STATUS} from '../../constant'
import InformativeLabel from '../../../../components/informative-label'
import {useProduct} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import WeeeFee from '../../../../components/weee-fee'

const OrderItemCard = ({
    item,
    status,
    isMultipleShipment = false,
    isItemReturnDeclined,
    editQuantity = false,
    returnRequested = false,
    controlled = {
        quantity: 0,
        setQuantity: () => {},
    },
    isReturnOrderForm = false,
    isReturnOrder = false,
    showDivider = false,
}) => {
    const {formatMessage, formatDate} = useIntl()
    const styles = useStyleConfig('ProductItem', {
        variant: 'OrderDetail',
        status,
        isMultipleShipment,
        isReturnOrderForm,
        isReturnOrder,
    })
    const {currency} = useCurrency()
    const isVATFlag = useBooleanCustomPreference('showVATFlag')

    // this is for legacy orders that don't have an image on OMS
    const image = useImageForLegacyOrder(item)

    const imageUrl = item?.officinaConfigurationImage__c || item?.ItemImage__c || image
    const weeeFee = item?.WeeeFee__c || 0

    // Check if item is preorder/backorder and should show availability date
    const isPreorderOrBackorder =
        item?.PurchaseType__c === 'Preorder' || item?.PurchaseType__c === 'Backorder'
    const isShippedOrDelivered =
        status?.toLowerCase() === SFCC_ORDER_STATUS.SHIPPED ||
        status?.toLowerCase() === SFCC_ORDER_STATUS.DELIVERED
    const showPreorderBackorderLabel =
        !isMultipleShipment &&
        isPreorderOrBackorder &&
        item?.ExpectedAvailabilityDate__c &&
        !isShippedOrDelivered

    return (
        <>
            {isItemReturnDeclined && (
                <Box display="flex" alignItems="center" gap="2">
                    <Text fontWeight="bold">
                        {formatMessage({
                            id: 'item_return_declined',
                            defaultMessage: 'Return:',
                        })}
                    </Text>
                    <InformativeLabel
                        label={'rejected'}
                        width="fit-content"
                        variant={getInfoLabelVariantByOmsStatus('declined')}
                    />
                </Box>
            )}
            {showPreorderBackorderLabel && (
                <Box display="flex" alignItems="center" {...styles.preorderBackorderLabel}>
                    <Text {...styles.preorderLabel}>
                        <FormattedMessage
                            id="order_summary.message.available_from"
                            defaultMessage="This item will be available from {date}"
                            values={{
                                date: (
                                    <Text as="span" fontWeight="bold">
                                        {formatDate(new Date(item?.ExpectedAvailabilityDate__c), {
                                            day: 'numeric',
                                            month: 'short',
                                            year: 'numeric',
                                        })}
                                    </Text>
                                ),
                            }}
                        />
                    </Text>
                </Box>
            )}
            <Flex sx={styles.flexContainer}>
                <Image src={imageUrl} alt="Image product test" {...styles.productImage} />
                <Box {...styles.detailContainer}>
                    <Stack gap={remCalc(4)}>
                        {item?.VariantIdentifier__c ||
                            (item?.Name && (
                                <Text {...styles.itemName}>
                                    {item.VariantIdentifier__c || item.Name}
                                </Text>
                            ))}
                        {item?.ProductCode && (
                            <Text {...styles.itemCode}>SKU {item.ProductCode}</Text>
                        )}
                    </Stack>
                    <Box {...styles.omniAndPrice}>
                        {/* Show discounted price if baseprice === listprice and adjustment != 0 */}
                        {item?.BasePrice__c === item?.ListPrice &&
                        item?.OriginalOrderItem?.TotalLineAdjustmentAmtWithTax !== 0 ? (
                            <UnitPrice
                                currency={item?.CurrencyIsoCode || currency}
                                styles={styles.itemPrice}
                                orderDetail={{
                                    UnitPrice: item?.OriginalOrderItem?.TotalAmtWithTax,
                                }}
                            />
                        ) : item?.UnitPrice ? (
                            <UnitPrice
                                currency={item?.CurrencyIsoCode || currency}
                                styles={styles.itemPrice}
                                orderDetail={{
                                    UnitPrice:
                                        status?.toLowerCase() === 'canceled'
                                            ? getItemPriceByStatus(status, item)
                                            : item?.ListPrice ||
                                              item?.OriginalOrderItem?.TotalAmtWithTax ||
                                              item?.TotalLineAmountWithTax,
                                }}
                            />
                        ) : null}
                        {/* Show striked price if baseprice > listprice or if baseprice === listprice and adjustment != 0 */}
                        {!!(
                            item?.BasePrice__c &&
                            (item?.BasePrice__c > item?.ListPrice ||
                                (item?.BasePrice__c === item?.ListPrice &&
                                    item?.OriginalOrderItem?.TotalLineAdjustmentAmtWithTax &&
                                    item?.OriginalOrderItem?.TotalLineAdjustmentAmtWithTax !== 0))
                        ) && (
                            <Box {...styles.omnibus}>
                                <Box {...styles.suggestedPrice}>
                                    <UnitPrice
                                        currency={item?.CurrencyIsoCode || currency}
                                        hasStrikethrough={true}
                                        orderDetail={{
                                            ListPrice: item?.BasePrice__c || item?.ListPrice,
                                        }}
                                    />
                                </Box>
                                <Text {...styles.suggestedText}>
                                    {formatMessage({
                                        id: 'item_variant.suggested_price',
                                        defaultMessage: 'Suggested Price',
                                    })}
                                </Text>
                            </Box>
                        )}
                    </Box>
                    {isVATFlag &&
                        ((item?.TotalAmtWithTax > 0 && item?.Quantity > 0) ||
                            (item?.OriginalOrderItem?.TotalAmtWithTax > 0 &&
                                item?.QuantityReturned > 0)) && (
                            <Box {...styles.vatDetails}>
                                <HStack color="inherit">
                                    <VatLabel
                                        amount={
                                            item?.Quantity > 0
                                                ? item?.TotalTaxAmount / item?.Quantity
                                                : item?.OriginalOrderItem?.TotalTaxAmount /
                                                  item?.QuantityReturned
                                        }
                                        fixedCurrency={item?.CurrencyIsoCode}
                                        vatRate={
                                            ((item?.Quantity > 0
                                                ? item?.TotalTaxAmount
                                                : item?.OriginalOrderItem?.TotalTaxAmount) /
                                                ((item?.Quantity > 0
                                                    ? item?.TotalAmtWithTax
                                                    : item?.OriginalOrderItem?.TotalAmtWithTax) -
                                                    (item?.Quantity > 0
                                                        ? item?.TotalTaxAmount
                                                        : item?.OriginalOrderItem
                                                              ?.TotalTaxAmount))) *
                                            100
                                        }
                                        variant="OrderDetail"
                                    />
                                </HStack>
                            </Box>
                        )}
                    {weeeFee ? (
                        <WeeeFee
                            itemCurrency={item?.CurrencyIsoCode}
                            price={weeeFee}
                            variant={'orderHistory'}
                        />
                    ) : (
                        <></>
                    )}
                    <Box {...styles.quantSelContainer}>
                        <Text
                            {...styles.textQuantity}
                            aria-label={formatMessage(
                                {
                                    id: 'item_variant.quantity.label',
                                    defaultMessage:
                                        'Quantity selector for {productName}. Selected quantity is {quantity}',
                                },
                                {
                                    quantity: item?.Quantity,
                                    productName: item?.Name,
                                },
                            )}
                        >
                            <FormattedMessage
                                defaultMessage="Quantity"
                                id="product_item.label.quantity"
                            />
                        </Text>
                        {editQuantity ? (
                            <QuantityPicker
                                min={1}
                                max={item?.QuantityAvailableToReturn}
                                variant={'OrderDetail'}
                                value={controlled?.quantity}
                                onChange={(newVal) => {
                                    controlled.setQuantity(newVal)
                                }}
                                isReturnOrder={isReturnOrder}
                            />
                        ) : (
                            <QuantityPicker
                                value={
                                    isReturnOrderForm
                                        ? item?.QuantityAvailableToReturn
                                        : returnRequested
                                        ? item?.QuantityExpected || item?.QuantityReturnInitiated
                                        : item?.QuantityAvailableToReturn ||
                                          item?.Quantity ||
                                          item?.QuantityOrdered
                                }
                                hideIncDecButtons={true}
                                variant={'OrderDetail'}
                                isReadOnly
                                isReturnOrder={isReturnOrder}
                            />
                        )}
                    </Box>
                </Box>
            </Flex>
            {showDivider && <Divider pt={remCalc(4)} marginBottom={remCalc(20)} />}
        </>
    )
}

OrderItemCard.propTypes = {
    item: PropTypes.object,
    status: PropTypes.string,
    isMultipleShipment: PropTypes.bool,
    isItemReturnDeclined: PropTypes.bool,
    editQuantity: PropTypes.bool,
    returnRequested: PropTypes.bool,
    controlled: PropTypes.shape({
        quantity: PropTypes.number,
        setQuantity: PropTypes.func,
    }),
    isReturnOrderForm: PropTypes.bool,
    isReturnOrder: PropTypes.bool,
    showDivider: PropTypes.bool,
}

export default OrderItemCard

const useImageForLegacyOrder = (item) => {
    const {currency} = useCurrency()
    const {locale} = useMultiSite()

    // only fetch product if we have an item with a product code and the ItemImage__c is null or undefined
    const shouldFetchProduct = item?.ProductCode && !item?.ItemImage__c

    const {data: product} = useProduct(
        {
            parameters: {
                id: item?.ProductCode,
                expand: ['images'],
                locale: locale.id,
                currency,
            },
        },
        {
            enabled: !!shouldFetchProduct, // ensure enabled is always a boolean
        },
    )
    const image =
        product &&
        findImageGroupBy(product?.imageGroups, {
            viewType: 'large',
            selectedVariationAttributes: product?.variationValues,
        })?.images?.[0]
    return image?.disBaseLink || image?.link
}
